import React from "react";

const entropyRows = [
  ["00", "source.status", "ONLINE", "99.997%"],
  ["01", "entropy.pool", "4096 bit", "rotating"],
  ["02", "sample.rate", "128 req/s", "reserved"],
  ["03", "checksum", "7F3A-91C0", "verified"],
];

const sectors = [
  [
    "01",
    "Cryptography",
    "Session keys, salts, nonce material, signing flows",
    "KEY",
    ["0101", "1110", "0011"],
  ],
  [
    "02",
    "Finance",
    "Monte Carlo workloads, risk sampling, draw settlement",
    "RISK",
    ["$", "Σ", "%"],
  ],
  [
    "03",
    "Gaming",
    "Auditable draws, fairness layers, player-facing randomness",
    "DRAW",
    ["[ ]", "[X]", "[ ]"],
  ],
  [
    "04",
    "Research",
    "Simulation seeds, sampling sets, reproducibility checks",
    "LAB",
    ["μ", "σ", "n"],
  ],
];

const plans = [
  ["START", "10k", "single key", "email support"],
  ["SYSTEM", "1m", "key rotation", "SLA queue"],
  ["ENTERPRISE", "custom", "dedicated pool", "private integration"],
];

const clientRows = [
  ["BANK-02", "financial simulation", "private beta", "EU"],
  ["CRYPT-11", "key material service", "pilot", "US"],
  ["LAB-04", "research sampling", "active", "UK"],
  ["DRAW-09", "auditable draws", "queued", "CA"],
];

const sampleBlocks = [
  "91 04 77 28 63 10 55 89",
  "02 41 76 18 33 90 12 64",
  "A7 F0 39 6C 11 88 C2 5D",
  "00 7F 13 4A 98 21 EE 09",
];

const tickerValues = ["07", "91", "44", "03", "88", "12", "64", "20"];

const navItems = [
  ["HOME", "#home"],
  ["TECHNOLOGY", "#technology"],
  ["CLIENTS", "#clients"],
  ["API", "#api"],
  ["PRICING", "#pricing"],
  ["CONTACT", "#contact"],
];

const scrollTo = (href) => {
  const element = document.querySelector(href);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const Home = () => {
  return (
    <main className="site-shell">
      <header className="topbar">
        <button className="wordmark" onClick={() => scrollTo("#home")}>
          T.Y.K.E
        </button>
        <nav className="nav-grid" aria-label="Primary navigation">
          {navItems.map(([label, href]) => (
            <button key={label} onClick={() => scrollTo(href)}>
              {label}
            </button>
          ))}
        </nav>
      </header>

      <section id="home" className="hero-panel">
        <div className="hero-meta">
          <span>TYKE://ENTROPY-LAB</span>
          <span>UTC SAMPLE WINDOW 00:00:08</span>
          <span>PUBLIC API NODE 01</span>
        </div>

        <div className="hero-grid">
          <div className="hero-copy">
            <div className="era-stamp" aria-hidden="true">
              <span>BUILD 2002.08</span>
              <span>MODE: MONO</span>
            </div>
            <p className="section-kicker">TRULY YIELDED KEY ENTROPY</p>
            <h1>T.Y.K.E</h1>
            <p className="hero-lede">
              Secure random number generation for systems where predictability
              is failure.
            </p>
            <div className="hero-actions">
              <button onClick={() => scrollTo("#api")}>READ API</button>
              <button onClick={() => scrollTo("#contact")}>REQUEST ACCESS</button>
            </div>
            <div className="ticker-strip" aria-label="Random sample ticker">
              {tickerValues.map((value, index) => (
                <span key={`${value}-${index}`}>{value}</span>
              ))}
            </div>
          </div>

          <div className="number-console" aria-label="Entropy sample">
            <div className="console-head">
              <span>entropy.sample</span>
              <span>live</span>
            </div>
            <div className="number-stack">
              {sampleBlocks.map((sample) => (
                <code key={sample}>{sample}</code>
              ))}
            </div>
            <div className="checksum-grid">
              <span>sha256</span>
              <strong>91C0.7F3A.44E2</strong>
              <span>latency</span>
              <strong>18ms</strong>
            </div>
            <div className="signal-meter" aria-label="Entropy signal meter">
              <span>entropy.signal</span>
              <div>
                <i></i>
                <i></i>
                <i></i>
                <i></i>
                <i></i>
                <i></i>
                <i></i>
                <i></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="technology" className="content-section">
        <div className="section-label">01 / TECHNOLOGY</div>
        <div className="split-layout">
          <div>
            <h2>Randomness as infrastructure.</h2>
            <p>
              T.Y.K.E exposes entropy as a small, verifiable service: request
              bytes, receive values, log the checksum, repeat without ceremony.
            </p>
            <pre className="ascii-diagram" aria-label="Entropy flow diagram">{`+---------+      +----------+      +---------+
| SOURCE  | ---> |  MIXER   | ---> |  API    |
+---------+      +----------+      +---------+
     |                |                |
  noise           checksum         response`}</pre>
          </div>
          <table className="data-table">
            <tbody>
              {entropyRows.map(([id, metric, value, note]) => (
                <tr key={id}>
                  <td>{id}</td>
                  <td>{metric}</td>
                  <td>{value}</td>
                  <td>{note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section id="use-cases" className="content-section">
        <div className="section-label">02 / USE CASES</div>
        <div className="matrix">
          {sectors.map(([id, title, text, label, glyphs]) => (
            <article key={title} className="matrix-cell">
              <div className="case-topline">
                <span>{id}</span>
                <span>{label}</span>
              </div>
              <div className="case-icon" aria-hidden="true">
                {glyphs.map((glyph) => (
                  <b key={glyph}>{glyph}</b>
                ))}
              </div>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="clients" className="content-section clients-section">
        <div className="section-label">03 / CLIENTS</div>
        <div className="directory-layout">
          <div>
            <h2>Private clients, visible audit trail.</h2>
            <p>
              Built for teams that need repeatable entropy records without a
              glossy dashboard between the request and the result.
            </p>
          </div>
          <table className="directory-table">
            <thead>
              <tr>
                <th>Node</th>
                <th>Use</th>
                <th>Status</th>
                <th>Zone</th>
              </tr>
            </thead>
            <tbody>
              {clientRows.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell) => (
                    <td key={cell}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section id="api" className="content-section">
        <div className="section-label">04 / API</div>
        <div className="api-layout">
          <div>
            <h2>HTTP in, numbers out.</h2>
            <p>
              The interface is intentionally plain: bearer token, request count,
              typed response, and values ready for downstream systems.
            </p>
          </div>
          <pre className="code-window">
            <code>{`curl https://api.tyke.com/v1/random?count=8 \\
  -H "Authorization: Bearer $TYKE_API_KEY"

{
  "request_id": "rq_91c07f3a",
  "values": [91, 04, 77, 28, 63, 10, 55, 89],
  "checksum": "7F3A-91C0",
  "generated_at": "2002-01-01T00:00:08Z"
}`}</code>
          </pre>
        </div>
      </section>

      <section id="pricing" className="content-section">
        <div className="section-label">05 / PRICING</div>
        <table className="pricing-table">
          <thead>
            <tr>
              <th>Plan</th>
              <th>Monthly volume</th>
              <th>Auth</th>
              <th>Support</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => (
              <tr key={plan[0]}>
                {plan.map((cell) => (
                  <td key={cell}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section id="contact" className="content-section contact-section">
        <div className="section-label">06 / CONTACT</div>
        <div className="contact-box">
          <h2>Request integration details.</h2>
          <p>
            Send expected volume, compliance needs, and integration timeline.
            We will return API access options and deployment notes.
          </p>
          <a href="mailto:info@arbok.tech">info@arbok.tech</a>
        </div>
      </section>

      <footer className="footer-bar">
        <span>T.Y.K.E</span>
        <span>LAST UPDATED 2002-08-17</span>
        <span>INFO@ARBOK.TECH</span>
      </footer>
    </main>
  );
};

export default Home;
